import { FC } from 'react'
import { Modal, Form, Input, Button, Alert, Typography, Result } from 'antd'
import { MailOutlined, CloseOutlined, MailTwoTone } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import './auth-modal.css'

const { Title, Text } = Typography

interface ForgotPasswordModalProps {
  visible: boolean
  onClose: () => void
  onBackToLogin: () => void
}

export const ForgotPasswordModal: FC<ForgotPasswordModalProps> = ({
  visible,
  onClose,
  onBackToLogin
}) => {
  const { t } = useTranslation(['common', 'auth'])
  const { resetPassword, resetPasswordState, clearResetPasswordState } = useAuth()
  const [form] = Form.useForm()

  const handleSubmit = async (values: { email: string }) => {
    const { success } = await resetPassword(values.email)
    if (success) {
      form.resetFields()
    }
  }

  const handleCancel = () => {
    clearResetPasswordState()
    form.resetFields()
    onClose()
  }

  const handleBackToLogin = () => {
    clearResetPasswordState()
    form.resetFields()
    onBackToLogin()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={resetPasswordState.success ? 500 : 400}
      centered
      closable={!resetPasswordState.success}
      maskClosable={!resetPasswordState.success}
      closeIcon={!resetPasswordState.success ? <CloseOutlined className="auth-modal__close-icon" /> : null}
    >
      {/* 重置链接发送成功状态 */}
      {resetPasswordState.success ? (
        <Result
          icon={<MailTwoTone twoToneColor="#52c41a" />}
          status="success"
          title={t('auth:forgotPassword.success')}
          subTitle={
            <div>
              <div>{t('auth:forgotPassword.successDescription')}</div>
              <div className="auth-modal__tips">
                <strong>{t('auth:forgotPassword.tips')}</strong>
                <ul className="auth-modal__tips-list">
                  <li>{t('auth:forgotPassword.tip1')}</li>
                  <li>{t('auth:forgotPassword.tip2')}</li>
                  <li>{t('auth:forgotPassword.tip3')}</li>
                </ul>
              </div>
            </div>
          }
          extra={[
            <Button key="back-to-login" onClick={handleBackToLogin}>
              {t('auth:button.backToLogin')}
            </Button>,
            <Button key="close" onClick={handleCancel}>
              {t('auth:button.gotIt')}
            </Button>
          ]}
        />
      ) : (
        <>
          <div className="auth-modal__header">
            <Title level={2} className="auth-modal__title">
              {t('auth:forgotPassword.title')}
            </Title>
            <Text type="secondary" className="auth-modal__subtitle">
              {t('auth:forgotPassword.subtitle')}
            </Text>
          </div>

          {resetPasswordState.error && (
            <Alert
              message={resetPasswordState.error}
              type="error"
              showIcon
              className="auth-modal__alert"
              closable
              onClose={clearResetPasswordState}
            />
          )}

          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              label={t('auth:forgotPassword.email')}
              rules={[
                { required: true, message: t('auth:validation.emailRequired') },
                { type: 'email', message: t('auth:validation.emailInvalid') }
              ]}
            >
              <Input
                prefix={<MailOutlined className="auth-modal__input-icon" />}
                placeholder={t('auth:forgotPassword.placeholder')}
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={resetPasswordState.loading}
                className="auth-modal__submit"
              >
                {t('auth:forgotPassword.submit')}
              </Button>
            </Form.Item>

            <div className="auth-modal__link-wrap auth-modal__link-wrap--spaced">
              <Button
                type="link"
                onClick={handleBackToLogin}
                className="auth-modal__link auth-modal__link--strong"
              >
                {t('auth:button.backToLogin')}
              </Button>
            </div>
          </Form>
        </>
      )}
    </Modal>
  )
}
